import { AxiosInstance } from 'axios'
import {
  AuthResponse,
  EmailPreferences,
  NextAction,
  SSOResponse,
  UserProfile,
  UserTotals
} from 'types/user'
import { USER_ENDPOINTS } from 'api/ApiRoutes'
import { cleanObject, formatDateParams } from 'utils/Functions'
import { GetTransactionsParams } from 'types/transactions'

class UsersApi {
  constructor(private axiosInstance: AxiosInstance) {}

  getCurrentUser = async () => {
    const d = await this.axiosInstance.get<UserProfile>(
      USER_ENDPOINTS.CURRENT_USER
    )
    return d.data
  }

  login = async (
    data:
      | {
          email: string
          password: string
          mixpanel_distinct_id?: string
          gt?: string | null
        }
      | { single_login_token: string; mixpanel_distinct_id?: string }
  ) => {
    const d = await this.axiosInstance.post<AuthResponse>(
      USER_ENDPOINTS.LOGIN,
      data
    )
    return d.data
  }

  oauthLogin = async ({
    id_token,
    authorization_code,
    platform = 'dashboard',
    provider
  }: {
    id_token?: string
    authorization_code: string
    platform?: 'dashboard'
    provider: 'apple' | 'google'
  }) => {
    const d = await this.axiosInstance.post<AuthResponse>(
      USER_ENDPOINTS.OAUTH_LOGIN,
      {
        id_token,
        platform,
        provider,
        authorization_code
      }
    )
    return d.data
  }

  disconnectOauthAccount = ({
    provider,
    passphrase
  }: {
    provider: string
    passphrase: string
  }) =>
    this.axiosInstance
      .delete(USER_ENDPOINTS.DISCONNECT_OAUTH(provider, passphrase))
      .then((d) => d.data)

  authAction = async (email: string) => {
    const { data } = await this.axiosInstance.get<NextAction>(
      USER_ENDPOINTS.AUTH_ACTION,
      {
        params: { email }
      }
    )
    return data
  }

  createUser = async ({
    email,
    password,
    gt,
    invite_token,
    mixpanel_distinct_id
  }: {
    email: string
    password: string
    gt?: string | null
    invite_token?: string | null
    mixpanel_distinct_id?: string
  }) => {
    const user = cleanObject({
      password,
      password_confirmation: password,
      email,
      gt,
      mixpanel_distinct_id,
      invite_token
    })
    const { data } = await this.axiosInstance.post<AuthResponse>(
      USER_ENDPOINTS.BASE,
      {
        user
      }
    )
    return data
  }

  updateUser = async (data: Partial<UserProfile>) => {
    const d = await this.axiosInstance.put<UserProfile>(
      USER_ENDPOINTS.CURRENT_USER,
      { user: { ...data } }
    )
    return d.data
  }

  getTotals = async (params: Partial<GetTransactionsParams>) => {
    // Disabled eslint validation until backend team align the params behavior
    // eslint-disable-next-line
    const { motusPurpose, motusHasAnomaly, purpose, date_range, ...rest } =
      params ?? {}
    const apiParams = cleanObject({
      ...rest,
      // Commented until backend team align the params behavior
      // has_anomaly: motusHasAnomaly,
      purpose:
        motusPurpose ?? (purpose !== 'Unclassified' ? purpose : undefined),
      ...formatDateParams(date_range ?? [])
    })
    const { data } = await this.axiosInstance.get<UserTotals>(
      USER_ENDPOINTS.CURRENT_USER_TOTALS,
      {
        params: apiParams
      }
    )
    return data
  }

  sso = async () => {
    const { data } = await this.axiosInstance.get<SSOResponse>(
      USER_ENDPOINTS.SSO
    )
    return data
  }

  ssoUpload = async ({ file }: { file: File }) => {
    const formData = new FormData()
    formData.append('saml_provider[idp_metadata_file]', file)
    const d = await this.axiosInstance.post<SSOResponse>(
      USER_ENDPOINTS.SSO,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' }
      }
    )
    return d.data
  }

  ssoEnableDisable = ({ enabled }: { enabled: boolean }) => {
    return this.axiosInstance.put<SSOResponse>(USER_ENDPOINTS.SSO, {
      saml_provider: {
        enabled
      }
    })
  }

  getEmailPreferences = async ({ tokenId }: { tokenId: string }) => {
    const d = await this.axiosInstance.get<EmailPreferences>(
      USER_ENDPOINTS.MANAGE_SUBSCRIPTIONS(tokenId)
    )
    return d.data
  }

  updateEmailPreferences = async ({
    tokenId,
    ...preferences
  }: {
    tokenId: string
  } & EmailPreferences) => {
    const d = await this.axiosInstance.put<EmailPreferences>(
      USER_ENDPOINTS.MANAGE_SUBSCRIPTIONS(tokenId),
      {
        ...preferences
      }
    )
    return d.data
  }

  requestPasswordReset = async (body: { email: string }) => {
    const d = await this.axiosInstance.post(
      USER_ENDPOINTS.REQUEST_PASSWORD_RESETS,
      body
    )
    return d.data
  }

  resetPassword = async ({
    tokenId,
    ...body
  }: {
    tokenId: string
    password: string
    password_confirmation: string
    onboarding?: boolean
  }) => {
    const d = await this.axiosInstance.put(
      USER_ENDPOINTS.RESET_PASSWORD(tokenId),
      body
    )
    return d.data
  }

  getIntercomToken = async () => {
    const d = await this.axiosInstance.post<{
      token: string
      expires_at: string
    }>(USER_ENDPOINTS.INTERCOM_TOKEN)
    return d.data
  }
}

export default UsersApi
